import React, {useState} from 'react';
import './LobbyInviteModal.css';
import PageButton from '../../elements/PageButton/PageButton';
import {buildLobbyInviteUrl} from '../../../utils/lobby/lobbyInviteUrl';
import {copyTextToClipboard} from '../../../utils/other/copyTextToClipboard';
import {getShareImageFile} from '../../../utils/share/getShareImageFile';

const PAGE_ID = 'LobbyInviteModal';

// Картинка к нативному «поделиться» — та же милитаристская иллюстрация, что у
// одноименной политики: приглашение зовет именно в бой
const SHARE_IMAGE_URL = '/img/policies/militarism.jpg';

const buildShareText = (inviteUrl) => `Присоединяйся ко мне в битву прямо сейчас! ${inviteUrl}`;

/**
 * Приглашение друга в лобби обычного сетевого матча: ссылка с кнопками
 * «Поделиться» (нативная панель с текстом и картинкой), «Скопировать» и
 * «Скрыть». Стилизована под модалку ежедневной награды за вход.
 *
 * Ссылка — реферальная ссылка приглашающего с дописанным лобби, поэтому
 * приглашение незарегистрированного игрока засчитывается как приглашение
 * друга (см. buildLobbyInviteUrl). Адресуется публичным реферальным кодом,
 * а не id игрока.
 */
const LobbyInviteModal = ({inviterReferalCode, lobbyId, onClose}) => {
  const [isCopied, setIsCopied] = useState(false);

  const inviteUrl = buildLobbyInviteUrl(inviterReferalCode, lobbyId);

  const handleShare = async () => {
    const shareText = buildShareText(inviteUrl);

    // Нативная панель «Поделиться»; без ее поддержки — фолбэк на копирование
    // сообщения целиком (тот же прием, что на экране приглашения друга)
    if (navigator.share) {
      try {
        const shareData = {text: shareText};

        // Картинка — необязательное дополнение: файлы это Web Share второго
        // уровня, их поддерживают не все браузеры и не все цели отправки.
        // canShare проверяем ровно с тем набором полей, который отправляем
        const imageFile = await getShareImageFile(SHARE_IMAGE_URL);
        if (imageFile && navigator.canShare?.({...shareData, files: [imageFile]})) {
          shareData.files = [imageFile];
        }

        await navigator.share(shareData);
        return;
      } catch (e) {
        // Отмена пользователем — не ошибка
        if (e.name === 'AbortError') return;
        console.error('Lobby invite share failed', e);
      }
    }

    try {
      await copyTextToClipboard(shareText);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (e) {
      console.error('Lobby invite share fallback copy failed', e);
      window.prompt('Скопируйте приглашение вручную:', shareText);
    }
  };

  const handleCopy = async () => {
    try {
      await copyTextToClipboard(inviteUrl);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (e) {
      console.error('Lobby invite copy failed', e);
      window.prompt('Не удалось скопировать автоматически. Скопируйте ссылку вручную:', inviteUrl);
    }
  };

  return (
    <div className="lobby-invite-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="lobby-invite-modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="lobby-invite-title">Пригласить друга в лобби</h2>
        <p className="lobby-invite-subtitle">
          Отправь ссылку — друг попадет прямо в это лобби.
        </p>

        <div className="lobby-invite-link">{inviteUrl}</div>
        <span className={`lobby-invite-copied ${isCopied ? 'lobby-invite-copied_visible' : ''}`}>
          Скопировано!
        </span>

        <div className="main-menu-buttons lobby-invite-buttons">
          <PageButton
            className="lobby-invite-button"
            onClick={handleShare}
            pageId={PAGE_ID}
            buttonId="share"
          >
            Поделиться
          </PageButton>
          <PageButton
            className="lobby-invite-button"
            onClick={handleCopy}
            pageId={PAGE_ID}
            buttonId="copy"
          >
            Скопировать
          </PageButton>
          <PageButton
            className="lobby-invite-button"
            onClick={onClose}
            pageId={PAGE_ID}
            buttonId="hide"
          >
            Скрыть
          </PageButton>
        </div>
      </div>
    </div>
  );
};

export default LobbyInviteModal;
